<template>
  <div class="wrapper">
    <!-- Header section -->
    <section class="section-shaped section-lg my-0 section-header">
      <div class="shape shape-style-1 shape-primary">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div class="container pt-lg-md">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <h1 class="display-3 text-white text-center">Terms and Conditions</h1>
            <p class="lead text-white text-center">Effective Date: May 3, 2025</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Terms and Conditions content -->
    <section class="section section-lg pt-lg-0 mt--100">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <card class="border-0" shadow body-classes="py-5 px-5">
              <div class="terms-content">
                <h2 class="text-primary mb-4">Terms and Conditions</h2>

                <div class="mb-5">
                  <h4>1. Eligibility</h4>
                  <p>To use Zwapit, you must:</p>
                  <ul>
                    <li>Be at least 16 years of age;</li>
                    <li>Hold a valid UK provisional or full driving licence; and</li>
                    <li>Have a DVSA-booked practical driving test you wish to swap with another user.</li>
                  </ul>
                </div>

                <div class="mb-5">
                  <h4>2. User Responsibilities</h4>
                  <ul>
                    <li>You are solely responsible for the accuracy of all information you submit to Zwapit.</li>
                    <li>Providing false, incomplete, or misleading information is strictly prohibited and may result in immediate suspension of your account.</li>
                    <li>You must comply with the DVSA's procedures for driving test swaps. Zwapit is not affiliated with DVSA and is a facilitation tool only.</li>
                  </ul>
                </div>

                <div class="mb-5">
                  <h4>3. Account Suspension & Appeals</h4>
                  <p>We reserve the right to suspend or terminate any account that breaches these Terms. Where an account is suspended, users may submit a written appeal by email. Zwapit will assess each appeal on a case-by-case basis. Our decision is final and binding.</p>
                </div>

                <div class="mb-5">
                  <h4>4. Definitions</h4>
                  <ul>
                    <li><strong>Swap:</strong> The process of requesting and agreeing to exchange DVSA driving test appointments between users.</li>
                    <li><strong>First User:</strong> The user who initiates a swap.</li>
                    <li><strong>Second User:</strong> The user who accepts the swap.</li>
                    <li><strong>DVSA:</strong> The Driver and Vehicle Standards Agency.</li>
                    <li><strong>Freeze Period:</strong> A 12-hour period where the driving test is held to allow the other user to complete payment and finalise the Match.</li>
                    <li><strong>Match:</strong> When both users have paid the admin fee, a Match occurs and contact details are revealed.</li>
                    <li><strong>Admin Fee:</strong> A non-refundable fee payable for using Zwapit services.</li>
                  </ul>
                </div>

                <div class="mb-5">
                  <h4>5. Swap Procedure</h4>
                  <ol>
                    <li>The First User selects a test to swap and submits a swap request.</li>
                    <li>Zwapit freezes the test of the First User for 12 hours to allow the Second User to respond to the request.</li>
                    <li>The Second User may accept or decline the request:
                      <ul>
                        <li>If declined: No fee is charged, and the request is cancelled.</li>
                        <li>If accepted: The Second User's test is also frozen for 12 hours. Both the First User and the Second User will each have 12 hours to complete their Admin Fee payments and finalise the Match.</li>
                      </ul>
                    </li>
                    <li>Once both users have paid the Admin Fee, a Match occurs, and contact details are revealed to both parties.</li>
                    <li>After a Match, the driving tests involved in that swap cannot be swapped again. Users who wish to swap again must make a new booking and initiate a new swap request.</li>
                    <li>The First User must contact DVSA by phone to officially complete the test swap.</li>
                  </ol>
                </div>

                <div class="mb-5">
                  <h4>6. Refunds Policy</h4>
                  <h5 class="mt-4">6.1 Refunds During Freeze Period</h5>
                  <ul>
                    <li>If a Match does not occur, the Second User may request a refund of the Admin Fee.</li>
                    <li>If the First User fails to complete payment during the 12-hour Freeze Period, the Second User may request a refund.</li>
                    <li>All refunds will be processed promptly but may take up to:
                      <ul>
                        <li>5 working days for UK accounts</li>
                        <li>21 working days for non-UK accounts</li>
                      </ul>
                    </li>
                  </ul>

                  <h5 class="mt-4">6.2 No Refund Scenarios</h5>
                  <ul>
                    <li>First Users are not entitled to refunds once the Admin Fee is paid.</li>
                    <li>No refunds will be issued once a Match has been completed.</li>
                    <li>If the First User fails to contact DVSA to complete the official swap, no refund will be given.</li>
                  </ul>
                </div>

                <div class="mb-5">
                  <h4>7. Disclaimers & Legal Protections</h4>
                  <ul>
                    <li>Zwapit is an independent third-party tool and is not endorsed, authorised, or affiliated with the DVSA or any government body.</li>
                    <li>Users are fully responsible for completing swaps in accordance with DVSA rules and procedures.</li>
                    <li>Zwapit does not guarantee the availability of test dates or successful completion of a swap.</li>
                    <li>We reserve the right to update these Terms at any time. Continued use after changes constitutes acceptance.</li>
                    <li>By agreeing to a Match, both the First User and Second User enter into a direct and binding agreement with one another for the purpose of facilitating a DVSA test swap. Zwapit is not a party to this agreement and acts solely as a technological intermediary. Both users expressly agree that any communications with the DVSA, including but not limited to calls to initiate or confirm the swap, are the sole responsibility of the users involved. In the event that the DVSA or any third party raises queries or objections regarding the test swap, both users agree that no liability, claim, or protest shall be brought against Zwapit, its owners, affiliates, or operators.</li>
                  </ul>
                </div>

                <div class="mb-5">
                  <h4>8. Governing Law</h4>
                  <p>These Terms shall be governed and construed in accordance with the laws of England and Wales. Disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
                </div>

                <div>
                  <h4>9. Contact</h4>
                  <p>For inquiries or support, contact Zwapit Support at: <a href="mailto:support@zwapit.com" class="text-primary">support@zwapit.com</a></p>
                </div>
              </div>
            </card>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Terms',
  components: {
    Card: () => import('@/components/Card')
  }
};
</script>

<style lang="scss">
.section-shaped {
  position: relative;
  overflow: hidden;

  .shape {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    &.shape-style-1 {
      span {
        position: absolute;
        
        &:nth-child(1) {
          bottom: -20px;
          left: -40px;
          width: 100px;
          height: 100px;
          opacity: 0.3;
          background: #017cfe;
          border-radius: 50%;
        }
        
        &:nth-child(2) {
          top: 10%;
          right: 10%;
          width: 150px;
          height: 150px;
          opacity: 0.2;
          background: #017cfe;
          border-radius: 50%;
        }
        
        &:nth-child(3) {
          top: 50%;
          right: 50%;
          width: 200px;
          height: 200px;
          background: rgba(1, 124, 254, 0.2);
          border-radius: 50%;
        }
        
        &:nth-child(4) {
          bottom: 50%;
          right: 10%;
          width: 300px;
          height: 300px;
          background: rgba(1, 124, 254, 0.15);
          border-radius: 50%;
        }
        
        &:nth-child(5) {
          bottom: 0;
          right: 0;
          width: 200px;
          height: 200px;
          background: rgba(1, 124, 254, 0.1);
        }
      }
    }

    &.shape-primary {
      background: linear-gradient(150deg, #015cbe 15%, #017cfe 70%, #56a8fe 94%);
    }
  }
}

.terms-content {
  h4 {
    color: #017cfe;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  h5 {
    color: #017cfe;
  }

  ul, ol {
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
}

.bg-gradient-default {
  background: linear-gradient(150deg, #015cbe 15%, #017cfe 70%, #56a8fe 94%) !important;
}

.text-primary {
  color: #017cfe !important;
}
</style> 